// src/utils/helpers.js

/**
 * Format a number as currency
 * @param {number} value - The value to format
 * @param {string} currency - Currency code (USD, EUR, etc.)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, currency = "USD") => {
  const num = Number(value) || 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
};

/**
 * Parse a date string in LOCAL timezone (avoids UTC shift issues)
 * This ensures "2026-01-01" is January 1st in the user's timezone, not December 31st
 * @param {string|Date} dateStr - The date string to parse
 * @returns {Date|null} Parsed Date object in local timezone, or null if invalid
 */
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  // Handle YYYY-MM-DD format - parse as local date
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  // Handle ISO strings with time - extract just the date part and parse locally
  if (typeof dateStr === 'string' && dateStr.includes('T')) {
    const datePart = dateStr.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  // If it's already a Date, return it
  if (dateStr instanceof Date) {
    return dateStr;
  }
  // Fallback for other formats
  const parsed = new Date(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Format a date string
 * @param {string|Date} dateValue - The date to format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateValue, options = {}) => {
  if (!dateValue) return "";

  try {
    // Use parseLocalDate for consistent timezone handling
    const date = parseLocalDate(dateValue);

    // Check if date is valid
    if (!date || isNaN(date.getTime())) return "";

    const defaultOptions = {
      year: "numeric",
      month: "short",
      day: "numeric",
      ...options,
    };

    return new Intl.DateTimeFormat(undefined, defaultOptions).format(date);
  } catch {
    return String(dateValue);
  }
};

/**
 * Format a date with time
 * @param {string|Date} dateValue - The date to format
 * @returns {string} Formatted date-time string
 */
export const formatDateTime = (dateValue) => {
  return formatDate(dateValue, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Compute the total purchase cost of an item
 * @param {object} item - Item with purchasePrice, taxes, shipping
 * @returns {number} Total purchase cost
 */
export const computePurchaseTotal = (item) => {
  if (!item) return 0;
  const price = Number(item.purchasePrice) || 0;
  const taxes = Number(item.taxes) || 0;
  const shipping = Number(item.shipping) || 0;
  return price + taxes + shipping;
};

/**
 * Compute the net profit of an item
 * @param {object} item - Item with cashoutPrice, purchasePrice, taxes, shipping
 * @returns {number} Net profit (can be negative)
 */
export const computeNetProfit = (item) => {
  if (!item) return 0;
  const cashout = Number(item.cashoutPrice) || 0;
  const purchaseTotal = computePurchaseTotal(item);
  return cashout - purchaseTotal;
};

/**
 * Debounce a function
 * @param {function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Generate a random ID
 * @param {number} length - Length of the ID
 * @returns {string} Random ID
 */
export const generateId = (length = 12) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const randomValues = new Uint32Array(length);
  crypto.getRandomValues(randomValues);
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(randomValues[i] % chars.length);
  }
  return result;
};

/**
 * Truncate a string to a maximum length
 * @param {string} str - String to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated string
 */
export const truncate = (str, maxLength = 50) => {
  if (!str) return "";
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + "...";
};

/**
 * Convert snake_case to camelCase
 * @param {string} str - String to convert
 * @returns {string} camelCase string
 */
export const snakeToCamel = (str) => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Convert camelCase to snake_case
 * @param {string} str - String to convert
 * @returns {string} snake_case string
 */
export const camelToSnake = (str) => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

/**
 * Deep clone an object
 * @param {object} obj - Object to clone
 * @returns {object} Cloned object
 */
export const deepClone = (obj) => {
  return JSON.parse(JSON.stringify(obj));
};

/**
 * Check if two objects are equal (shallow comparison)
 * @param {object} obj1 - First object
 * @param {object} obj2 - Second object
 * @returns {boolean} Whether objects are equal
 */
export const shallowEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;
  
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  
  if (keys1.length !== keys2.length) return false;
  
  return keys1.every((key) => obj1[key] === obj2[key]);
};

/**
 * Parse a number from a string, handling currency symbols
 * @param {string} str - String to parse
 * @returns {number} Parsed number
 */
export const parseNumber = (str) => {
  if (typeof str === "number") return str;
  if (!str) return 0;
  
  // Remove currency symbols, commas, spaces
  const cleaned = String(str).replace(/[^0-9.-]/g, "");
  const num = parseFloat(cleaned);
  
  return isNaN(num) ? 0 : num;
};

/**
 * Format a number with thousands separators
 * @param {number} num - Number to format
 * @returns {string} Formatted number
 */
export const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

/**
 * Calculate percentage
 * @param {number} value - Current value
 * @param {number} total - Total value
 * @returns {number} Percentage (0-100)
 */
export const calculatePercentage = (value, total) => {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
};

/**
 * Group array items by a key
 * @param {array} array - Array to group
 * @param {string|function} key - Key to group by
 * @returns {object} Grouped object
 */
export const groupBy = (array, key) => {
  return array.reduce((result, item) => {
    const groupKey = typeof key === "function" ? key(item) : item[key];
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(item);
    return result;
  }, {});
};

/**
 * Sort array by multiple keys
 * @param {array} array - Array to sort
 * @param {array} keys - Array of key objects { key, order: 'asc'|'desc' }
 * @returns {array} Sorted array
 */
export const sortBy = (array, keys) => {
  return [...array].sort((a, b) => {
    for (const { key, order = 'asc' } of keys) {
      const aVal = a[key];
      const bVal = b[key];
      
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Add months to a date, handling month-end edge cases correctly.
 * Example: Jan 31 + 1 month = Feb 28 (or 29 in leap year), not March 3
 * @param {Date} date - The date to modify
 * @param {number} months - Number of months to add
 */
const addMonthsSafe = (date, months) => {
  const originalDay = date.getDate();

  // Move to first of month to avoid overflow issues
  date.setDate(1);
  date.setMonth(date.getMonth() + months);

  // Get the last day of the new month
  const lastDayOfNewMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  // Set day to original day or last day of month, whichever is smaller
  date.setDate(Math.min(originalDay, lastDayOfNewMonth));
};

/**
 * Calculate the next due date based on a recurring cycle
 * Handles month-end edge cases correctly (e.g., Jan 31 + 1 month = Feb 28/29)
 * @param {string} currentDate - Current due date in YYYY-MM-DD format
 * @param {string} cycle - Recurring cycle: '1 Day', '1 Week', '1 Month', '3 Months', '6 Months', '1 Year'
 * @returns {string} Next due date in YYYY-MM-DD format
 */
export const calculateNextDueDate = (currentDate, cycle) => {
  if (!currentDate || !cycle) return currentDate;

  const date = new Date(currentDate + 'T00:00:00');

  switch (cycle) {
    case '1 Day':
      date.setDate(date.getDate() + 1);
      break;
    case '1 Week':
      date.setDate(date.getDate() + 7);
      break;
    case '1 Month':
      addMonthsSafe(date, 1);
      break;
    case '3 Months':
      addMonthsSafe(date, 3);
      break;
    case '6 Months':
      addMonthsSafe(date, 6);
      break;
    case '1 Year':
      // Handle leap year edge case (Feb 29 + 1 year = Feb 28 in non-leap year)
      addMonthsSafe(date, 12);
      break;
    default:
      // Default to 1 month if unknown cycle
      addMonthsSafe(date, 1);
  }

  // Format back to YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

/**
 * Get today's date in YYYY-MM-DD format (local time)
 * @returns {string} Today's date as YYYY-MM-DD
 */
export const getTodayString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Normalize date to YYYY-MM-DD string to avoid timezone issues
 * @param {string|Date} d - Date value
 * @returns {string} YYYY-MM-DD string
 */
export const normalizeDateString = (d) => {
  if (!d) return '';
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
  if (typeof d === 'string' && d.includes('T')) return d.split('T')[0];
  try {
    const date = new Date(d);
    if (!isNaN(date.getTime())) {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
  } catch (e) {}
  return String(d);
};

/**
 * "Days in stock" badge for inventory and vault rows. Returns null when no
 * date is set or the date is in the future. The 0–7 day band is green
 * (fresh), 8–30 yellow (consider pricing), and 31+ red (stale).
 *
 * Timezone-correct: parses YYYY-MM-DD strings as LOCAL midnight so the
 * day-count is calculated against the user's calendar day. The previous
 * implementation used `new Date(dateStr)` which interprets bare date
 * strings as UTC midnight — for users in negative offsets (e.g. America/
 * Chicago = UTC-6) this caused items purchased the same evening to be
 * reported as "1d old" the following morning.
 */
export const getAgeBadge = (purchaseDate) => {
  if (!purchaseDate) return null;
  let date;
  // YYYY-MM-DD → local midnight on that calendar day. Anything else (full
  // ISO timestamp, Date object) goes through the standard parse — those
  // already carry timezone info so the diff is correct.
  if (typeof purchaseDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(purchaseDate)) {
    const [y, m, d] = purchaseDate.split('-').map(Number);
    date = new Date(y, m - 1, d);
  } else {
    date = new Date(purchaseDate);
  }
  if (isNaN(date.getTime())) return null;
  const now = new Date();
  const days = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (days < 0) return null;
  if (days <= 7) return {
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    label: `${days}d`,
    tooltip: `${days} day${days !== 1 ? 's' : ''} in stock - Fresh inventory`,
  };
  if (days <= 30) return {
    color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    label: `${days}d`,
    tooltip: `${days} days in stock - Consider pricing competitively`,
  };
  return {
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    label: `${days}d`,
    tooltip: `${days} days in stock - Stale inventory, consider discounting`,
  };
};

/**
 * Heuristic guard against mis-categorizing sealed TCG products as singles.
 *
 * Why this exists: the UPC lookup AI and the email scraper both occasionally
 * tag products like "Pokemon Booster Box" or "Charizard ETB" as `TCG Singles`
 * when they're actually sealed `Collectibles`. Singles should be one card —
 * sealed products are boxes, packs, tins, etc. We post-process every save
 * with this helper so a single source of truth fixes both new imports going
 * forward and any spot that calls convertToDb.
 *
 * Only fires when category === 'TCG Singles', so it won't accidentally
 * reclassify Sneakers/Electronics. Returns the (possibly corrected) category.
 */
const SEALED_TCG_KEYWORDS = [
  // Pokemon / TCG sealed product names
  'booster box', 'booster bundle', 'booster pack',
  'elite trainer', 'etb',
  'collection box', 'premium collection', 'collector chest',
  'theme deck', 'structure deck', 'starter deck', 'starter set', 'deck box',
  'tin', // covers "mini tin", "collector tin"
  'blister', // covers "3-pack blister", "blister pack"
  'display', // covers "display box", "display case"
  'sealed',
  'gift set', 'mystery box', 'mystery pack',
  'hobby box', 'blaster box', 'blaster pack',
  'mega box', 'hanger box', 'fat pack', 'value pack', 'cello pack',
  'booster', // catches "Pokemon TCG Booster"
];

export const correctSealedCollectibleCategory = (name, category) => {
  if (category !== 'TCG Singles') return category;
  const n = (name || '').toLowerCase();
  if (!n) return category;
  for (const kw of SEALED_TCG_KEYWORDS) {
    if (n.includes(kw)) return 'Collectibles';
  }
  return category;
};

/**
 * Sanitize search input to prevent SQL injection via LIKE patterns
 * @param {string} input - Search input string
 * @returns {string} Sanitized string
 */
export const sanitizeSearchInput = (input) => {
  if (!input) return '';
  return String(input)
    .replace(/\\/g, '\\\\')
    .replace(/%/g, '\\%')
    .replace(/_/g, '\\_');
};

export default {
  formatCurrency,
  formatDate,
  formatDateTime,
  computePurchaseTotal,
  computeNetProfit,
  debounce,
  generateId,
  truncate,
  snakeToCamel,
  camelToSnake,
  deepClone,
  shallowEqual,
  parseNumber,
  formatNumber,
  calculatePercentage,
  groupBy,
  sortBy,
  calculateNextDueDate,
  getTodayString,
  normalizeDateString,
  getAgeBadge,
  sanitizeSearchInput,
  correctSealedCollectibleCategory,
};